/**
 * Floor Plan Card Selected Epic
 *
 * Reacts to floorPlanCardSelected actions and dispatches navigateTo to switch to floor plan
 * detail with the relevant selected lot / context preserved. Every floor plan card (including a
 * Similar Home one under the Sold chip, NHS20-8827) opens the chip experience its own lots
 * actually belong to — mirroring housingUnitCardSelectedEpic.js's targetChip resolution — so when
 * the active chip isn't already the right one, this first dispatches chipSelected(targetChip)
 * (its existing reducer resets to a fresh listing for it) before navigateTo, which then pushes
 * that fresh state as the view-history entry Back returns to, instead of the Sold card the user
 * actually clicked from. This also matters for NHS20-9017: the detail footer's price-hiding check
 * keys off the active chip, so a Model-only plan landing on Available here would also wrongly
 * show a price.
 *
 * A HousePlanId can be shared by lots of different statuses — e.g. a Model home built from the
 * same plan as several Available lots (NHS20-8882) — so "any lot with this planId" is not enough
 * to pin a single lot; it must be the lot matching the status of wherever this click is actually
 * navigating to. `targetChip` resolves that destination status first: Available/Model pass
 * through unchanged (clicking a card from its own chip's listing); otherwise (a Sold-chip
 * Similar Home click, or any other non-Available/Model chip) it defaults to Available UNLESS
 * every Available/Model-eligible lot on this plan is actually Model-status — a "Model-only" plan
 * (a Model home with no housingUnitId yet, still rendered as a floor-plan card — see below) has no
 * Available lot to land on at all, so forcing Available here left `matchingStatusLot` unable to
 * find anything, `selectedLotId` null, and the lot card empty (reported live on community 189099,
 * plan "The Pinehurst" — Model-only, single lot, no Available lot in the community at all).
 * `matchingStatusLot` then looks up the one lot on this plan carrying the resolved status. This
 * keeps activeViewContext.selectedLotId — and therefore the map's selected-lot highlight and the
 * lot card (see redux/selectors.js#selectCurrentlySelectedLot) — in sync with the status actually
 * being browsed, instead of whichever lot happened to be found first regardless of status. Falls
 * back to the prior carry-over behavior when no lot on this plan carries the destination status
 * (shouldn't happen once targetChip resolution above accounts for a Model-only plan, but kept as
 * a defensive fallback) — a generic Available floor plan can span many lots, so there's no single
 * one to pin ahead of an explicit map click.
 *
 * A Model home without a housingUnitId yet is still rendered as a floor-plan card (it has no
 * housing unit to key a QMI-style card off of — see housingUnitCardSelectedEpic.js), including as
 * a Similar Home candidate (redux/selectors.js#selectSimilarHomesCandidates' isFloorPlan branch)
 * even when every lot it's buildable on is Model-status.
 *
 * This same action$ also fires for a plan card clicked from lotMapSelection.plugin.js's own plan
 * picker — that view renders its plan cards with the exact same [data-plan-id] markup
 * (components/floorPlanCards.dom.js), so floorPlanDetail.plugin.js's single, view-agnostic
 * [data-plan-id] click listener (bound once in start(), not scoped to any one view owning
 * [data-panel]) catches those clicks too, and they arrive here exactly like a listing-card click.
 * lotMapSelection only ever exists because a specific lot was already clicked on the map (a lot
 * buildable with more than one plan — see services/lotRoutingService.js), and
 * activeViewContext.selectedLotId still holds it untouched at this point (lotMapSelection.plugin.js
 * only reads it, never clears it). `preferredLot` below honors that already-selected lot instead
 * of blindly picking "the first matching-status lot for this plan" — without it, choosing a plan
 * buildable on many lots (e.g. every lot in the community) from that picker landed on whichever
 * lot happened to be first in the lots array, discarding the exact lot the user had just clicked
 * (reported live on community 165424, plan 1965186).
 *
 * @param {object} params
 * @param {object} params.dataActions - Actions from the feature data slice instance
 * @param {Observable} params.store$ - Redux store state observable
 * @returns {import('redux-observable').Epic}
 */

import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { ofType } from 'redux-observable';
import { VIEW_FLOOR_PLAN_DETAIL, VIEW_ENTRY_SOURCE_CARD_CLICK, VIEW_ENTRY_SOURCE_OTHER } from '../constants';
import { selectSelectedLotId, selectLots, selectActiveChip } from './selectors';
import LotStatus from '../enums/lotStatus';

/**
 * Resolves which chip a floor-plan card click should navigate into, based on the plan's own
 * lots rather than blindly defaulting to Available (NHS20-8882 live regression, community
 * 189099 "The Pinehurst" — a Model-only plan with zero Available lots; also the underlying cause
 * NHS20-9017 hit from the price-hiding-footer angle). Requires EVERY Available/Model-eligible lot
 * on the plan to be Model before targeting Model — a plan with a mix of statuses (the NHS20-8882
 * Case 1 data: Floor Plan 4 / lot 6 Model + lot 85 Available) must still default to Available;
 * "does this plan have any Model lot at all" is not a safe enough check on its own.
 * @param {object[]} lots - selectLots(state)
 * @param {number} planId
 * @returns {string} A LotStatus.Available/Model string.
 */
function resolveFloorPlanCardTargetChip(lots, planId) {
  const eligibleStatuses = new Set(
    lots
      .filter((lot) => lot.housePlanIds?.includes(planId) && (
        lot.status === LotStatus.Available.toString() || lot.status === LotStatus.Model.toString()
      ))
      .map((lot) => lot.status)
  );
  return eligibleStatuses.size === 1 && eligibleStatuses.has(LotStatus.Model.toString())
    ? LotStatus.Model.toString()
    : LotStatus.Available.toString();
}

function createFloorPlanCardSelectedEpic({ dataActions, store$ }) {
  return (action$) => action$.pipe(
    ofType(dataActions.floorPlanCardSelected.type),
    withLatestFrom(store$),
      mergeMap(([action, state]) => {
          const activeChip = selectActiveChip(state);
          const planId = Number(action.payload);
          const lots = selectLots(state);

          // A Similar Home click only ever happens from the Sold chip's own listing (Sold cards
          // themselves aren't clickable — see components/soldHomeCard.dom.js) and always targets
          // an unrelated plan, so the previously selected sold lot must not carry over into the
          // new detail view (NHS20-8827 selectedLotId leak).
          const isSimilarHomeClick = activeChip === LotStatus.Sold.toString();

          const targetChip = activeChip === LotStatus.Available.toString() || activeChip === LotStatus.Model.toString()
              ? activeChip
              : resolveFloorPlanCardTargetChip(lots, planId);

          // Honor a lot already selected on the map (e.g. via lotMapSelection.plugin.js's plan
          // picker) when it actually qualifies for this plan/chip, rather than guessing a
          // different lot for the same plan. Similar Home clicks are excluded on purpose — any
          // carried-over selection there belongs to an unrelated Sold lot, never this plan.
          const currentSelectedLotId = selectSelectedLotId(state);
          const currentSelectedLot = !isSimilarHomeClick && currentSelectedLotId
              ? lots.find((lot) => lot.sitePlanLotId === currentSelectedLotId)
              : null;
          const preferredLot = currentSelectedLot &&
              currentSelectedLot.status === targetChip &&
              currentSelectedLot.housePlanIds?.includes(planId)
            ? currentSelectedLot
            : null;

          const matchingStatusLot = preferredLot ?? lots.find((lot) => (
              lot.status === targetChip && lot.housePlanIds?.includes(planId)));

          const selectedLotId = matchingStatusLot?.sitePlanLotId ??
              (isSimilarHomeClick ? null : (currentSelectedLotId ?? null));
          const navigateToAction = dataActions.navigateTo({
              view: VIEW_FLOOR_PLAN_DETAIL,
              context: {
                  planId: Number.isFinite(planId) ? planId : null,
                  selectedLotId,
                  housingUnitId: null,
                  source: isSimilarHomeClick ? VIEW_ENTRY_SOURCE_OTHER : VIEW_ENTRY_SOURCE_CARD_CLICK,
              },
          });

      return targetChip === activeChip
        ? of(navigateToAction)
        : of(dataActions.chipSelected(targetChip), navigateToAction);
    }),
  );
}

export default createFloorPlanCardSelectedEpic;
